import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Paper,
  Tabs,
  Tab,
  Link,
  IconButton,
  useTheme,
  useMediaQuery,
  CardActions,
  Alert,
  Divider,
} from '@mui/material';
import AuthorsSidebar from '../components/AuthorsSidebar';
import {
  Description,
  CheckCircle,
  Timeline,
  Person,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  School as SchoolIcon,
  Book as BookIcon,
  Lightbulb as LightbulbIcon,
  Link as LinkIcon,
  LocationOn as LocationOnIcon,
  TrendingUp as TrendingUpIcon,
  SwipeLeft as SwipeLeftIcon,
  SwipeRight as SwipeRightIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Info,
} from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';

// Стилизованные компоненты
const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const publicationSteps = [
  {
    title: 'Подготовка статьи',
    description: (
      <Box>
          С требованиями можно ознакомиться в разделе{' '}
          <Link 
            component={RouterLink} 
            to="/authors/rules" 
            sx={{ 
              textDecoration: 'none', 
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            "ПРАВИЛА ПУБЛИКАЦИИ"
          </Link>
      </Box>
    ),
    icon: <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '1rem' }}>1</Avatar>,
  },
  {
    title: (
      <Link 
        component={RouterLink} 
        to="/register" 
        sx={{ 
          textDecoration: 'none', 
          color: 'inherit',
          '&:hover': {
            color: 'primary.main',
            textDecoration: 'underline'
          }
        }}
      >
        Регистрация
      </Link>
    ),
    description: 'Зарегистрируйтесь на сайте или войдите в личный кабинет',
    icon: <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '1rem' }}>2</Avatar>,
  },
  {
    title: 'Отправка материалов',
    description: 'Загрузите статью и необходимые метаданные через личный кабинет',
    icon: <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '1rem' }}>3</Avatar>,
  },
  {
    title: 'Заключение договора',
    description: 'Подпишите авторский договор с редакцией журнала',
    icon: <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '1rem' }}>4</Avatar>,
  },
  {
    title: 'Рецензирование',
    description: 'Дождитесь результатов рецензирования и внесите правки при необходимости',
    icon: <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '1rem' }}>5</Avatar>,
  },
];

const usefulMaterials = [
  {
    title: 'Как написать хорошую научную статью',
    description: 'Руководство по структуре и содержанию научных статей',
    icon: <BookIcon />,
    link: 'https://example.com/writing-guide',
  },
  {
    title: 'Популярные научные журналы',
    description: 'Список рейтинговых журналов по различным направлениям',
    icon: <SchoolIcon />,
    link: 'https://example.com/journals',
  },
  {
    title: 'Советы по рецензированию',
    description: 'Как подготовить статью к рецензированию',
    icon: <LightbulbIcon />,
    link: 'https://example.com/review-tips',
  },
  {
    title: 'Полезные ресурсы',
    description: 'Ссылки на полезные инструменты и материалы',
    icon: <LinkIcon />,
    link: 'https://example.com/resources',
  },
];



const navigationSections = [
  {
    title: 'КАК ОПУБЛИКОВАТЬ СТАТЬЮ',
    icon: <Description />,
    link: '/authors'
  },
  {
    title: 'ПУБЛИКАЦИОННАЯ ЭТИКА',
    icon: <CheckCircle />,
    link: '/authors/ethics'
  },
  {
    title: 'ПРАВИЛА ПУБЛИКАЦИИ',
    icon: <BookIcon />,
    link: '/authors/rules',
    // items: [
    //   'Редакционные правила для авторов',
    //   'Правила подготовки рукописей',
    //   'Соблюдение этических норм',
    //   'Оформление информации',
    //   'Электронная корректура'
    // ]
  },
  // {
  //   title: 'OPEN ACCESS',
  //   icon: <LinkIcon />,
  //   link: '/authors/open-access',
  //   items: [
  //     'Что такое Open Access',
  //     'Преимущества Open Access',
  //     'Лицензии Open Access',
  //     'Как опубликовать статью'
  //   ]
  // },
  // {
  //   title: 'АВТОРСКИЙ ДОГОВОР',
  //   icon: <Person />,
  //   link: '/authors/agreement'
  // },
  // {
  //   title: 'ИСПОЛЬЗОВАНИЕ ЗАИМСТВОВАННЫХ МАТЕРИАЛОВ',
  //   icon: <LightbulbIcon />,
  //   link: '/authors/borrowed-materials'
  // },
  // {
  //   title: 'СВЯЗЬ С РЕДАКЦИЕЙ',
  //   icon: <EmailIcon />,
  //   link: '/authors/contact'
  // },
  // {
  //   title: 'ВЫПЛАТЫ ГОНОРАРОВ',
  //   icon: <TrendingUpIcon />,
  //   link: '/authors/royalties'
  // },
  // {
  //   title: 'ВОПРОСЫ-ОТВЕТЫ',
  //   icon: <QuestionAnswerIcon />,
  //   link: '/authors/faq'
  // }
];

const Authors: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleSwipeLeft = () => {
    if (selectedTab < 1) {
      setSelectedTab(selectedTab + 1);
    }
  };

  const handleSwipeRight = () => {
    if (selectedTab > 0) {
      setSelectedTab(selectedTab - 1);
    }
  };

  return (
    <AuthorsSidebar>
      <Container sx={{ py: 4 }}>
        <Box sx={{ position: 'relative' }}>
          {isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <IconButton 
                onClick={handleSwipeRight} 
                disabled={selectedTab === 0}
                sx={{ opacity: selectedTab === 0 ? 0.3 : 1 }}
              >
                <SwipeLeftIcon />
              </IconButton>
              <IconButton 
                onClick={handleSwipeLeft} 
                disabled={selectedTab === 1}
                sx={{ opacity: selectedTab === 1 ? 0.3 : 1 }}
              >
                <SwipeRightIcon />
              </IconButton>
            </Box>
          )}
          
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => handleTabChange(newValue)}
            sx={{ mb: 4 }}
            variant={isMobile ? "fullWidth" : "standard"}
          >
            <Tab 
              label={isMobile ? "Информация" : "Информация для авторов"} 
              icon={isMobile ? <Person /> : undefined}
            />
            <Tab 
              label={isMobile ? "Материалы" : "Полезные материалы"} 
              icon={isMobile ? <BookIcon /> : undefined}
            />
          </Tabs>
        </Box>

        {selectedTab === 0 ? (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              {/* Общие сведения */}
              <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Общие сведения
                </Typography>
                <Typography variant="body1" paragraph>
                  Обращаем внимание авторов на то, что публикации статей в журнале «Тюменский научный журнал» осуществляется 
                  при условии соблюдения правил подготовки статей и при положительном решении редколлегии по итогам 
                  рецензирования и с учетом очередности присылаемых для публикации материалов.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Важная информация:
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="DOI (Digital Object Identifier)"
                        secondary="Каждой статье присваивается DOI — цифровой идентификатор объекта, мировой стандарт отображения данных в Интернете."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Срок первичного рассмотрения рукописи"
                        secondary="До 10 рабочих дней"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Требования к оригинальности"
                        secondary="Редакция не принимает к рассмотрению статьи с низким уровнем оригинальности текста. Текст должен отличаться минимум на 30% от ранее опубликованных материалов. Рукописи должны иметь оригинальность не менее 80%, что должно подтверждаться отчетом в системе Антиплагиат."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Объем статьи"
                        secondary="Тексты статей принимаются объемом от 8 до 15 машинописных страниц (не менее 10000 и не более 30000 знаков с учетом пробелов). Список литературы в расчете объема статьи не учитывается. В расчет объема статьи входят аннотация, ключевые слова, библиография, сведения об авторе(ах)."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Качество оформления"
                        secondary="Статья должна быть написана качественно, аккуратно оформлена и тщательно отредактирована."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Отправка материалов"
                        secondary="Электронные материалы статьи отправляются через Личный кабинет, копии материалов рекомендуется отправить на электронный адрес: tyumen-science@yandex.ru; TSJournal@yandex.ru. Печатные версии отправляются по адресу г.Тюмень, ул.Одесская, д.9, оф.511, 625023"
                      />
                    </ListItem>
                  </List>
                </Box>
              </Paper>

              {/* Процесс публикации */}
              <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Процесс публикации
                </Typography>
                <List>
                  {publicationSteps.map((step, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>{step.icon}</ListItemIcon>
                      <ListItemText
                        primary={step.title}
                        secondary={step.description}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Требуемые материалы */}
              <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Требуемые материалы для отправки
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    В файле Рукописи статьи (в соответствии с правилами оформления статей) высылаются:
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
                    Материалы электронной версии статьи отправляются через Личный кабинет, копии материалов рекомендуется отправить на электронный адрес: tyumen-science@yandex.ru; TSJournal@yandex.ru
                  </Alert>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    I. Электронная версия рукописи (в формате Word), которая должна иметь:
                  </Typography>
                  <List sx={{ ml: 2 }}>
                    <ListItem>
                      <ListItemText 
                        primary="А. Аннотацию (на русском и английском) от 150 до 200 слов"
                        secondary="Аннотация должна иметь информативный характер и отражать актуальность, цели научного исследования, используемую методологию, основные научные результаты, полученные автором (-ами) и их практическое значение."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Б. Ключевые слова (на русском и английском языках) – 7–10 ключевых слов или словосочетаний"
                        secondary="Ключевые слова должны отражать основное содержание статьи, определять предметную область исследования, встречаться в тексте статьи."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="В. Сведения об авторах: ФИО, их должности, ученые степени, ученые звания, место работы и адрес электронной почты (на русском и английском языках)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Г. Список литературы должен состоять не менее чем из 10 источников"
                        secondary="Приветствуется наличие в библиографическом списке зарубежных изданий."
                      />
                    </ListItem>
                  </List>
                  
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Структура статьи, как правило, включает:
                  </Typography>
                  <List sx={{ ml: 2 }}>
                    <ListItem>
                      <ListItemText primary="Введение (характеристика актуальности темы и степени ее научной разработанности), указание на цель и задачи данной статьи" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Методы: обозначение и изложение использованных методов, математических моделей, программных комплексов" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Результаты: представление результатов экспериментов, графическая интерпретация, полученные аналитические зависимости и т.д." />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Обсуждение результатов" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Выводы, заключение" />
                    </ListItem>
                  </List>
                  
                  <Alert severity="info" sx={{ mt: 2, ml: 2 }}>
                    Для аспирантов, соискателей ученой степени кандидата наук в обязательном порядке необходимо указывать: 
                    телефоны кафедр, научных подразделений, к которым они прикреплены; данные о научном руководителе 
                    (фамилия, имя, отчество, ученая степень, звание).
                  </Alert>
                </Box>
                <Divider sx={{ my: 4, height: 1, bgcolor: 'grey.400' }} />

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    II. Печатный вариант рукописи
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    Скан-версия статьи, распечатанной на бумаге, полностью идентичной электронному варианту. 
                    На печатном варианте автор (-ы) должен (-ны) собственноручно поставить подпись, которая означает 
                    согласие на передачу прав на издание и распространение содержащейся в статье информации редакции Журнала. 
                    Поступление статьи в редакцию означает согласие автора с правилами Журнала.
                  </Typography>
                </Box>
                <Divider sx={{ my: 4, height: 1, bgcolor: 'grey.400' }} />

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    III. Сопроводительные документы
                  </Typography>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2, ml: 2 }}>
                    Вместе с рукописью статьи необходимо отправить:
                  </Typography>
                  <List sx={{ ml: 2 }}>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Авторское соглашение (установленной формы)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Согласие на обработку персональных данных" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Согласие на размещение статьи в системе РИНЦ" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Отчет о проверке в системе Антиплагиат (Full) (при наличии)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Рецензия специалиста в предметной области (по теме) исследования – доктора наук, профессора (при наличии) (установленной формы)*" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Экспертное заключение"
                        secondary="От организации, представляющей статью к публикации, о возможности опубликования материалов исследования в открытой печати, подтверждающее, что статья не содержит никакой секретной информации."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Сопроводительное письмо"
                        secondary="На фирменном бланке организации, в которой выполнена работа, где указывается, что публикация статьи в Журнале не нарушает ничьих прав, что статья ранее нигде не была опубликована, не направлялась и не будет направляться для опубликования в другие научные издания, что при подготовке статьи соблюдены требования публикационной этики, что автор дает свое согласие на обработку персональных данных редакции Журнала."
                      />
                    </ListItem>
                  </List>
                  <Alert severity="warning" sx={{ mt: 2, ml: 2 }}>
                    <Typography variant="body2">
                      <strong>Для аспирантов и магистров дополнительно требуется:</strong>
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="Отзыв научного руководителя" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Справка с места учебы, подтверждающая обучение в аспирантуре (магистратуре), с указанием сроков обучения, подписанная руководителем организации" />
                      </ListItem>
                    </List>
                  </Alert>
                </Box>
              </Paper>
            </Grid>
          </Grid>
      ) : (
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Полезные материалы для авторов
              </Typography>
              <Typography variant="body1" paragraph>
                Здесь собраны полезные материалы, которые помогут вам в написании и публикации научных статей.
              </Typography>
              
              {isMobile ? (
                // Мобильная версия - карточки
                <Grid container spacing={2}>
                  {usefulMaterials.map((material, index) => (
                    <Grid item xs={12} key={index}>
                      <Card sx={{ mb: 2 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {material.icon}
                            <Typography variant="h6" sx={{ ml: 1 }}>
                              {material.title}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {material.description}
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            component={Link}
                            href={material.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<LinkIcon />}
                          >
                            Открыть
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                // Десктопная версия - список
                <List>
                  {usefulMaterials.map((material, index) => (
                    <ListItem 
                      key={index} 
                      component={Link} 
                      href={material.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ 
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <ListItemIcon>{material.icon}</ListItemIcon>
                      <ListItemText
                        primary={material.title}
                        secondary={material.description}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
      </Container>
    </AuthorsSidebar>
  );
};

export default Authors; 